import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, exhaustMap, withLatestFrom, filter } from 'rxjs/operators';

import { StripeGatewayService } from '../../services/gateway/stripe.gateway.service';
import * as SubscriptionActions from './subscription.actions';
import * as AuthActions from '../auth/auth.actions';
import { selectSubscriptionAutoLoadBlocked } from './subscription.selectors';

@Injectable()
export class SubscriptionEffects {
  constructor(private actions$: Actions, private stripeGateway: StripeGatewayService, private store: Store) {}

  // Load subscription status effect
  // Uses exhaustMap to prevent concurrent requests - only one HTTP request at a time
  // exhaustMap ignores new loadSubscriptionStatus actions while a request is in progress
  loadSubscriptionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.loadSubscriptionStatus),
      // Use exhaustMap to ignore new requests while one is in progress
      // This ensures only one HTTP request happens at a time, even if multiple actions are dispatched
      // The reducer sets loading=true, and exhaustMap prevents concurrent requests
      exhaustMap(() =>
        this.stripeGateway.getSubscriptionStatus().pipe(
          map((subscription) => {
            // If we got subscription data (even if null), mark as loaded
            // subscription can be null if user doesn't have a subscription yet
            return SubscriptionActions.loadSubscriptionStatusSuccess({ subscription });
          }),
          catchError((error) => {
            // Don't mark as loaded on error - we need subscriptionId to be present
            return of(
              SubscriptionActions.loadSubscriptionStatusFailure({
                error: error.message || 'Failed to load subscription status',
              })
            );
          })
        )
      )
    )
  );

  // Clear subscription on logout
  clearSubscriptionOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => SubscriptionActions.clearSubscription())
    )
  );

  // Auto-load subscription status on login success
  loadSubscriptionStatusOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      withLatestFrom(this.store.select(selectSubscriptionAutoLoadBlocked)),
      filter(([, blocked]) => !blocked),
      map(() => SubscriptionActions.loadSubscriptionStatus())
    )
  );
}
